// Theme business logic — framework-agnostic, zero React imports

import { themeStore } from '@core/stores/theme';
import type { ThemeColors } from '@core/stores/theme';
import type {
  ThemeId,
  ThemeDefinition,
  ThemeSchemeColors,
  ThemeEditorState,
  ThemeEditorErrors,
  ThemeExport,
  ThemePreview,
  TokenGroup,
} from './types';

// ============================================
// Storage
// ============================================

const THEME_ID_KEY = 'app_theme_id';
const CUSTOM_THEMES_KEY = 'app_custom_themes';

// ============================================
// Built-in Theme Definitions
// ============================================

/** Default theme — current platform colors (shadcn neutral) */
const defaultTheme: ThemeDefinition = {
  id: 'default',
  nameKey: 'theme.default.name',
  descriptionKey: 'theme.default.description',
  source: 'builtin',
  light: {
    base: {
      primary: '222.2 47.4% 11.2%',
      primaryForeground: '210 40% 98%',
      secondary: '210 40% 96.1%',
      secondaryForeground: '222.2 47.4% 11.2%',
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      muted: '210 40% 96.1%',
      mutedForeground: '215.4 16.3% 46.9%',
      accent: '210 40% 96.1%',
      accentForeground: '222.2 47.4% 11.2%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '210 40% 98%',
      border: '214.3 31.8% 91.4%',
      input: '214.3 31.8% 91.4%',
      ring: '222.2 84% 4.9%',
      card: '0 0% 100%',
      cardForeground: '222.2 84% 4.9%',
      popover: '0 0% 100%',
      popoverForeground: '222.2 84% 4.9%',
    },
    sidebar: {
      sidebar: '220 14.3% 95.9%',
      sidebarForeground: '222.2 84% 4.9%',
      sidebarBorder: '214.3 31.8% 91.4%',
      sidebarAccent: '210 40% 96.1%',
      sidebarAccentForeground: '222.2 47.4% 11.2%',
    },
    rail: {
      rail: '0 0% 100%',
      railForeground: '222.2 84% 4.9%',
      railBorder: '214.3 31.8% 91.4%',
    },
  },
  dark: {
    base: {
      primary: '210 5% 90%',
      primaryForeground: '220 4% 10%',
      secondary: '220 3% 17%',
      secondaryForeground: '210 5% 90%',
      background: '220 4% 10%',
      foreground: '210 5% 90%',
      muted: '220 3% 17%',
      mutedForeground: '220 2% 52%',
      accent: '220 3% 20%',
      accentForeground: '210 5% 90%',
      destructive: '0 62% 35%',
      destructiveForeground: '210 5% 90%',
      border: '220 3% 19%',
      input: '220 3% 17%',
      ring: '220 4% 62%',
      card: '220 4% 13%',
      cardForeground: '210 5% 90%',
      popover: '220 4% 13%',
      popoverForeground: '210 5% 90%',
    },
    sidebar: {
      sidebar: '220 4% 8%',
      sidebarForeground: '210 5% 90%',
      sidebarBorder: '220 3% 16%',
      sidebarAccent: '220 3% 18%',
      sidebarAccentForeground: '210 5% 90%',
    },
    rail: {
      rail: '220 4% 8%',
      railForeground: '210 5% 90%',
      railBorder: '220 3% 16%',
    },
  },
  preview: {
    primary: '222.2 47.4% 11.2%',
    background: '0 0% 100%',
    sidebar: '220 14.3% 95.9%',
    accent: '210 40% 96.1%',
  },
};

/** Ocean Blue theme — bright blue, clean feel */
const oceanTheme: ThemeDefinition = {
  id: 'ocean',
  nameKey: 'theme.ocean.name',
  descriptionKey: 'theme.ocean.description',
  source: 'builtin',
  light: {
    base: {
      primary: '211 100% 50%', // #0077FF
      primaryForeground: '0 0% 100%',
      secondary: '214 32% 95%', // #EBEDF0
      secondaryForeground: '216 20% 20%',
      background: '0 0% 100%',
      foreground: '216 20% 15%',
      muted: '216 20% 95%', // #F0F2F5
      mutedForeground: '216 12% 47%',
      accent: '211 100% 95%', // light blue tint
      accentForeground: '211 100% 35%',
      destructive: '0 76% 55%',
      destructiveForeground: '0 0% 100%',
      border: '214 25% 90%',
      input: '214 25% 90%',
      ring: '211 100% 50%',
      card: '0 0% 100%',
      cardForeground: '216 20% 15%',
      popover: '0 0% 100%',
      popoverForeground: '216 20% 15%',
    },
    sidebar: {
      sidebar: '214 32% 95%', // #EBEDF0
      sidebarForeground: '216 20% 20%',
      sidebarBorder: '214 25% 89%',
      sidebarAccent: '211 100% 95%',
      sidebarAccentForeground: '211 100% 35%',
    },
    rail: {
      rail: '0 0% 100%',
      railForeground: '216 20% 15%',
      railBorder: '214 25% 90%',
    },
  },
  dark: {
    base: {
      primary: '211 100% 55%', // brighter blue for dark
      primaryForeground: '0 0% 100%',
      secondary: '216 20% 16%',
      secondaryForeground: '214 20% 88%',
      background: '216 25% 10%',
      foreground: '214 20% 92%',
      muted: '216 20% 16%',
      mutedForeground: '214 15% 60%',
      accent: '211 60% 20%',
      accentForeground: '211 100% 75%',
      destructive: '0 62% 35%',
      destructiveForeground: '0 0% 95%',
      border: '216 20% 18%',
      input: '216 20% 18%',
      ring: '211 100% 55%',
      card: '216 25% 12%',
      cardForeground: '214 20% 92%',
      popover: '216 25% 12%',
      popoverForeground: '214 20% 92%',
    },
    sidebar: {
      sidebar: '216 25% 8%',
      sidebarForeground: '214 20% 88%',
      sidebarBorder: '216 20% 16%',
      sidebarAccent: '211 60% 20%',
      sidebarAccentForeground: '211 100% 75%',
    },
    rail: {
      rail: '216 25% 12%',
      railForeground: '214 20% 92%',
      railBorder: '216 20% 18%',
    },
  },
  preview: {
    primary: '211 100% 50%',
    background: '0 0% 100%',
    sidebar: '214 32% 95%',
    accent: '211 100% 95%',
  },
};

/** Amethyst theme — purple/magenta, vibrant */
const amethystTheme: ThemeDefinition = {
  id: 'amethyst',
  nameKey: 'theme.amethyst.name',
  descriptionKey: 'theme.amethyst.description',
  source: 'builtin',
  light: {
    base: {
      primary: '280 82% 55%', // #A73AFD purple
      primaryForeground: '0 0% 100%',
      secondary: '280 30% 96%',
      secondaryForeground: '280 40% 20%',
      background: '0 0% 100%',
      foreground: '280 20% 12%',
      muted: '280 20% 96%',
      mutedForeground: '280 10% 46%',
      accent: '280 82% 95%', // light purple tint
      accentForeground: '280 82% 38%',
      destructive: '0 76% 55%',
      destructiveForeground: '0 0% 100%',
      border: '280 15% 91%',
      input: '280 15% 91%',
      ring: '280 82% 55%',
      card: '0 0% 100%',
      cardForeground: '280 20% 12%',
      popover: '0 0% 100%',
      popoverForeground: '280 20% 12%',
    },
    sidebar: {
      sidebar: '280 25% 96%',
      sidebarForeground: '280 20% 15%',
      sidebarBorder: '280 15% 90%',
      sidebarAccent: '280 82% 94%',
      sidebarAccentForeground: '280 82% 38%',
    },
    rail: {
      rail: '0 0% 100%',
      railForeground: '280 20% 12%',
      railBorder: '280 15% 91%',
    },
  },
  dark: {
    base: {
      primary: '280 82% 65%', // brighter purple for dark
      primaryForeground: '0 0% 100%',
      secondary: '280 25% 16%',
      secondaryForeground: '280 20% 88%',
      background: '280 30% 7%',
      foreground: '280 15% 92%',
      muted: '280 25% 16%',
      mutedForeground: '280 12% 58%',
      accent: '280 50% 18%',
      accentForeground: '280 82% 78%',
      destructive: '0 62% 35%',
      destructiveForeground: '0 0% 95%',
      border: '280 25% 17%',
      input: '280 25% 17%',
      ring: '280 82% 65%',
      card: '280 30% 9%',
      cardForeground: '280 15% 92%',
      popover: '280 30% 9%',
      popoverForeground: '280 15% 92%',
    },
    sidebar: {
      sidebar: '280 30% 6%',
      sidebarForeground: '280 15% 88%',
      sidebarBorder: '280 25% 15%',
      sidebarAccent: '280 50% 18%',
      sidebarAccentForeground: '280 82% 78%',
    },
    rail: {
      rail: '280 30% 9%',
      railForeground: '280 15% 92%',
      railBorder: '280 25% 17%',
    },
  },
  preview: {
    primary: '280 82% 55%',
    background: '0 0% 100%',
    sidebar: '280 25% 96%',
    accent: '280 82% 95%',
  },
};

/** Business Neutral theme — slate/charcoal, conservative professional */
const businessTheme: ThemeDefinition = {
  id: 'business',
  nameKey: 'theme.business.name',
  descriptionKey: 'theme.business.description',
  source: 'builtin',
  light: {
    base: {
      primary: '215 28% 27%', // #334155 slate-700
      primaryForeground: '210 40% 98%',
      secondary: '210 18% 95%',
      secondaryForeground: '215 25% 27%',
      background: '210 20% 99%', // very slightly warm white
      foreground: '215 28% 17%',
      muted: '210 18% 95%',
      mutedForeground: '215 16% 47%',
      accent: '210 18% 93%',
      accentForeground: '215 25% 27%',
      destructive: '0 65% 51%',
      destructiveForeground: '0 0% 100%',
      border: '214 20% 90%',
      input: '214 20% 90%',
      ring: '215 28% 27%',
      card: '0 0% 100%',
      cardForeground: '215 28% 17%',
      popover: '0 0% 100%',
      popoverForeground: '215 28% 17%',
    },
    sidebar: {
      sidebar: '210 18% 96%',
      sidebarForeground: '215 28% 20%',
      sidebarBorder: '214 20% 90%',
      sidebarAccent: '210 18% 93%',
      sidebarAccentForeground: '215 25% 30%',
    },
    rail: {
      rail: '0 0% 100%',
      railForeground: '215 28% 17%',
      railBorder: '214 20% 90%',
    },
  },
  dark: {
    base: {
      primary: '215 20% 75%', // lighter slate for dark mode
      primaryForeground: '215 28% 12%',
      secondary: '215 20% 16%',
      secondaryForeground: '210 18% 88%',
      background: '215 28% 8%',
      foreground: '210 18% 90%',
      muted: '215 20% 16%',
      mutedForeground: '215 15% 58%',
      accent: '215 20% 18%',
      accentForeground: '210 18% 82%',
      destructive: '0 55% 35%',
      destructiveForeground: '0 0% 95%',
      border: '215 20% 17%',
      input: '215 20% 17%',
      ring: '215 20% 65%',
      card: '215 28% 10%',
      cardForeground: '210 18% 90%',
      popover: '215 28% 10%',
      popoverForeground: '210 18% 90%',
    },
    sidebar: {
      sidebar: '215 28% 6%',
      sidebarForeground: '210 18% 85%',
      sidebarBorder: '215 20% 15%',
      sidebarAccent: '215 20% 18%',
      sidebarAccentForeground: '210 18% 82%',
    },
    rail: {
      rail: '215 28% 10%',
      railForeground: '210 18% 90%',
      railBorder: '215 20% 17%',
    },
  },
  preview: {
    primary: '215 28% 27%',
    background: '210 20% 99%',
    sidebar: '210 18% 96%',
    accent: '210 18% 93%',
  },
};

// ============================================
// Theme Registry
// ============================================

const BUILTIN_THEMES: ThemeDefinition[] = [
  defaultTheme,
  oceanTheme,
  amethystTheme,
  businessTheme,
];

/** Get all built-in themes */
export function getBuiltinThemes(): ThemeDefinition[] {
  return BUILTIN_THEMES;
}

/** Get a built-in theme by ID */
export function getBuiltinTheme(id: ThemeId): ThemeDefinition | undefined {
  return BUILTIN_THEMES.find((t) => t.id === id);
}

/** Get all themes (built-in + custom) */
export function getAllThemes(): ThemeDefinition[] {
  return [...BUILTIN_THEMES, ...loadCustomThemes()];
}

/** Get a theme by ID (built-in or custom) */
export function getTheme(id: ThemeId): ThemeDefinition | undefined {
  return getBuiltinTheme(id) ?? loadCustomThemes().find((t) => t.id === id);
}

// ============================================
// Theme Application
// ============================================

/** Apply a theme by ID — sets colors on ThemeStore + sidebar/rail CSS vars */
export function applyTheme(id: ThemeId): void {
  const theme = getTheme(id);
  if (!theme) return;

  const resolvedScheme = themeStore.resolvedTheme;
  const scheme = resolvedScheme === 'dark' ? theme.dark : theme.light;

  // Apply base colors via ThemeStore
  if (scheme.base) {
    themeStore.setColors({
      light: theme.light.base as Partial<ThemeColors>,
      dark: theme.dark.base as Partial<ThemeColors>,
    });
  }

  // Apply sidebar and rail tokens directly as CSS custom properties
  applySidebarRailTokens(theme, resolvedScheme);

  saveActiveThemeId(id);
}

/** Apply sidebar/rail CSS custom properties that ThemeStore doesn't cover */
function applySidebarRailTokens(
  theme: ThemeDefinition,
  scheme: 'light' | 'dark',
): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const colors = scheme === 'dark' ? theme.dark : theme.light;

  if (colors.sidebar) {
    const s = colors.sidebar;
    if (s.sidebar) root.style.setProperty('--sidebar', s.sidebar);
    if (s.sidebarForeground) root.style.setProperty('--sidebar-foreground', s.sidebarForeground);
    if (s.sidebarBorder) root.style.setProperty('--sidebar-border', s.sidebarBorder);
    if (s.sidebarAccent) root.style.setProperty('--sidebar-accent', s.sidebarAccent);
    if (s.sidebarAccentForeground) root.style.setProperty('--sidebar-accent-foreground', s.sidebarAccentForeground);
  }

  if (colors.rail) {
    const r = colors.rail;
    if (r.rail) root.style.setProperty('--rail', r.rail);
    if (r.railForeground) root.style.setProperty('--rail-foreground', r.railForeground);
    if (r.railBorder) root.style.setProperty('--rail-border', r.railBorder);
  }
}

/** Reset to default theme */
export function resetTheme(): void {
  themeStore.resetColors();
  applyTheme('default');
}

// ============================================
// Persistence
// ============================================

/** Load active theme ID from storage */
export function loadActiveThemeId(): ThemeId {
  try {
    return localStorage.getItem(THEME_ID_KEY) ?? 'default';
  } catch {
    return 'default';
  }
}

/** Save active theme ID to storage */
export function saveActiveThemeId(id: ThemeId): void {
  try {
    localStorage.setItem(THEME_ID_KEY, id);
  } catch {
    // localStorage unavailable
  }
}

/** Load custom themes from storage */
export function loadCustomThemes(): ThemeDefinition[] {
  try {
    const stored = localStorage.getItem(CUSTOM_THEMES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/** Save custom themes to storage */
function saveCustomThemes(themes: ThemeDefinition[]): void {
  try {
    localStorage.setItem(CUSTOM_THEMES_KEY, JSON.stringify(themes));
  } catch {
    // localStorage unavailable
  }
}

// ============================================
// Custom Theme CRUD
// ============================================

/** Create a new custom theme from editor state */
export function createCustomTheme(state: ThemeEditorState): ThemeDefinition {
  const id = `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const now = new Date().toISOString();

  const theme: ThemeDefinition = {
    id,
    nameKey: state.name, // custom themes use raw name, not i18n key
    descriptionKey: state.description,
    source: 'custom',
    light: state.light,
    dark: state.dark,
    preview: generatePreview(state.light),
    createdAt: now,
    updatedAt: now,
  };

  const customs = loadCustomThemes();
  customs.push(theme);
  saveCustomThemes(customs);

  return theme;
}

/** Update an existing custom theme */
export function updateCustomTheme(id: ThemeId, state: ThemeEditorState): ThemeDefinition | undefined {
  const customs = loadCustomThemes();
  const idx = customs.findIndex((t) => t.id === id);
  if (idx === -1) return undefined;

  customs[idx] = {
    ...customs[idx],
    nameKey: state.name,
    descriptionKey: state.description,
    light: state.light,
    dark: state.dark,
    preview: generatePreview(state.light),
    updatedAt: new Date().toISOString(),
  };

  saveCustomThemes(customs);
  return customs[idx];
}

/** Delete a custom theme */
export function deleteCustomTheme(id: ThemeId): boolean {
  const customs = loadCustomThemes();
  const filtered = customs.filter((t) => t.id !== id);
  if (filtered.length === customs.length) return false;

  saveCustomThemes(filtered);

  // If deleted theme was active, switch to default
  if (loadActiveThemeId() === id) {
    applyTheme('default');
  }

  return true;
}

/** Duplicate a theme as starting point for customization */
export function cloneTheme(sourceId: ThemeId, newName: string): ThemeDefinition | undefined {
  const source = getTheme(sourceId);
  if (!source) return undefined;

  const state: ThemeEditorState = {
    name: newName,
    description: '',
    baseThemeId: sourceId,
    light: structuredClone(source.light),
    dark: structuredClone(source.dark),
    activeScheme: 'light',
  };

  return createCustomTheme(state);
}

// ============================================
// Theme Editor Helpers
// ============================================

/** Create initial editor state from an existing theme (for editing or cloning) */
export function createEditorState(themeId?: ThemeId): ThemeEditorState {
  const source = themeId ? getTheme(themeId) : getTheme('default');
  const base = source ?? getTheme('default')!;

  return {
    name: source?.source === 'custom' ? source.nameKey : '',
    description: source?.source === 'custom' ? source.descriptionKey : '',
    baseThemeId: base.id,
    light: structuredClone(base.light),
    dark: structuredClone(base.dark),
    activeScheme: 'light',
  };
}

/** Validate theme editor state */
export function validateThemeEditor(state: ThemeEditorState): ThemeEditorErrors {
  const errors: ThemeEditorErrors = {};

  if (!state.name.trim()) {
    errors.name = 'theme.editor.validation.nameRequired';
  } else if (state.name.length > 50) {
    errors.name = 'theme.editor.validation.nameTooLong';
  }

  if (state.description.length > 200) {
    errors.description = 'theme.editor.validation.descriptionTooLong';
  }

  // Validate HSL format for all color values
  const validateColors = (scheme: ThemeSchemeColors, prefix: string) => {
    if (scheme.base) {
      for (const [key, value] of Object.entries(scheme.base)) {
        if (value && !isValidHSL(value)) {
          errors[`${prefix}.base.${key}`] = 'theme.editor.validation.invalidColor';
        }
      }
    }
    if (scheme.sidebar) {
      for (const [key, value] of Object.entries(scheme.sidebar)) {
        if (value && !isValidHSL(value as string)) {
          errors[`${prefix}.sidebar.${key}`] = 'theme.editor.validation.invalidColor';
        }
      }
    }
    if (scheme.rail) {
      for (const [key, value] of Object.entries(scheme.rail)) {
        if (value && !isValidHSL(value as string)) {
          errors[`${prefix}.rail.${key}`] = 'theme.editor.validation.invalidColor';
        }
      }
    }
  };

  validateColors(state.light, 'light');
  validateColors(state.dark, 'dark');

  return errors;
}

/** Validate HSL string format: "H S% L%" */
export function isValidHSL(value: string): boolean {
  const parts = value.trim().split(/\s+/);
  if (parts.length !== 3) return false;

  const h = parseFloat(parts[0]);
  const s = parts[1];
  const l = parts[2];

  if (isNaN(h) || h < 0 || h > 360) return false;
  if (!s.endsWith('%')) return false;
  if (!l.endsWith('%')) return false;

  const sVal = parseFloat(s);
  const lVal = parseFloat(l);

  if (isNaN(sVal) || sVal < 0 || sVal > 100) return false;
  if (isNaN(lVal) || lVal < 0 || lVal > 100) return false;

  return true;
}

/** Convert hex color (#RRGGBB) to HSL string "H S% L%" */
export function hexToHSL(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0 0% 0%';

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Convert HSL string "H S% L%" to hex color "#RRGGBB" */
export function hslToHex(hslStr: string): string {
  const parts = hslStr.trim().split(/\s+/);
  if (parts.length !== 3) return '#000000';

  const h = parseFloat(parts[0]) / 360;
  const s = parseFloat(parts[1]) / 100;
  const l = parseFloat(parts[2]) / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Generate preview from light scheme colors */
function generatePreview(light: ThemeSchemeColors): ThemePreview {
  return {
    primary: light.base?.primary ?? '0 0% 0%',
    background: light.base?.background ?? '0 0% 100%',
    sidebar: light.sidebar?.sidebar ?? '0 0% 95%',
    accent: light.base?.accent ?? '0 0% 95%',
  };
}

// ============================================
// Export / Import
// ============================================

/** Export a theme as JSON for sharing */
export function exportTheme(id: ThemeId): ThemeExport | undefined {
  const theme = getTheme(id);
  if (!theme) return undefined;

  return {
    version: 1,
    name: theme.nameKey,
    description: theme.descriptionKey,
    light: theme.light,
    dark: theme.dark,
  };
}

/** Import a theme from JSON */
export function importTheme(data: unknown): ThemeDefinition | undefined {
  if (!isValidThemeExport(data)) return undefined;

  const state: ThemeEditorState = {
    name: data.name,
    description: data.description,
    baseThemeId: 'default',
    light: data.light,
    dark: data.dark,
    activeScheme: 'light',
  };

  const errors = validateThemeEditor(state);
  if (Object.keys(errors).length > 0) return undefined;

  return createCustomTheme(state);
}

/** Validate theme export data structure */
function isValidThemeExport(data: unknown): data is ThemeExport {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return (
    obj.version === 1 &&
    typeof obj.name === 'string' &&
    typeof obj.description === 'string' &&
    typeof obj.light === 'object' &&
    typeof obj.dark === 'object' &&
    obj.light !== null &&
    obj.dark !== null
  );
}

// ============================================
// Token Groups (for editor UI)
// ============================================

/** Token groups for the theme editor interface */
export const TOKEN_GROUPS: TokenGroup[] = [
  {
    id: 'surface',
    labelKey: 'theme.editor.group.surface',
    tokens: [
      { key: 'background', labelKey: 'theme.editor.token.background', descriptionKey: 'theme.editor.token.backgroundDesc', cssVar: '--background', group: 'base' },
      { key: 'foreground', labelKey: 'theme.editor.token.foreground', descriptionKey: 'theme.editor.token.foregroundDesc', cssVar: '--foreground', group: 'base' },
      { key: 'card', labelKey: 'theme.editor.token.card', descriptionKey: 'theme.editor.token.cardDesc', cssVar: '--card', group: 'base' },
      { key: 'cardForeground', labelKey: 'theme.editor.token.cardForeground', descriptionKey: 'theme.editor.token.cardForegroundDesc', cssVar: '--card-foreground', group: 'base' },
    ],
  },
  {
    id: 'brand',
    labelKey: 'theme.editor.group.brand',
    tokens: [
      { key: 'primary', labelKey: 'theme.editor.token.primary', descriptionKey: 'theme.editor.token.primaryDesc', cssVar: '--primary', group: 'base' },
      { key: 'primaryForeground', labelKey: 'theme.editor.token.primaryForeground', descriptionKey: 'theme.editor.token.primaryForegroundDesc', cssVar: '--primary-foreground', group: 'base' },
      { key: 'accent', labelKey: 'theme.editor.token.accent', descriptionKey: 'theme.editor.token.accentDesc', cssVar: '--accent', group: 'base' },
      { key: 'accentForeground', labelKey: 'theme.editor.token.accentForeground', descriptionKey: 'theme.editor.token.accentForegroundDesc', cssVar: '--accent-foreground', group: 'base' },
    ],
  },
  {
    id: 'secondary',
    labelKey: 'theme.editor.group.secondary',
    tokens: [
      { key: 'secondary', labelKey: 'theme.editor.token.secondary', descriptionKey: 'theme.editor.token.secondaryDesc', cssVar: '--secondary', group: 'base' },
      { key: 'secondaryForeground', labelKey: 'theme.editor.token.secondaryForeground', descriptionKey: 'theme.editor.token.secondaryForegroundDesc', cssVar: '--secondary-foreground', group: 'base' },
      { key: 'muted', labelKey: 'theme.editor.token.muted', descriptionKey: 'theme.editor.token.mutedDesc', cssVar: '--muted', group: 'base' },
      { key: 'mutedForeground', labelKey: 'theme.editor.token.mutedForeground', descriptionKey: 'theme.editor.token.mutedForegroundDesc', cssVar: '--muted-foreground', group: 'base' },
    ],
  },
  {
    id: 'feedback',
    labelKey: 'theme.editor.group.feedback',
    tokens: [
      { key: 'destructive', labelKey: 'theme.editor.token.destructive', descriptionKey: 'theme.editor.token.destructiveDesc', cssVar: '--destructive', group: 'base' },
      { key: 'destructiveForeground', labelKey: 'theme.editor.token.destructiveForeground', descriptionKey: 'theme.editor.token.destructiveForegroundDesc', cssVar: '--destructive-foreground', group: 'base' },
    ],
  },
  {
    id: 'controls',
    labelKey: 'theme.editor.group.controls',
    tokens: [
      { key: 'border', labelKey: 'theme.editor.token.border', descriptionKey: 'theme.editor.token.borderDesc', cssVar: '--border', group: 'base' },
      { key: 'input', labelKey: 'theme.editor.token.input', descriptionKey: 'theme.editor.token.inputDesc', cssVar: '--input', group: 'base' },
      { key: 'ring', labelKey: 'theme.editor.token.ring', descriptionKey: 'theme.editor.token.ringDesc', cssVar: '--ring', group: 'base' },
    ],
  },
  {
    id: 'sidebar',
    labelKey: 'theme.editor.group.sidebar',
    tokens: [
      { key: 'sidebar', labelKey: 'theme.editor.token.sidebar', descriptionKey: 'theme.editor.token.sidebarDesc', cssVar: '--sidebar', group: 'sidebar' },
      { key: 'sidebarForeground', labelKey: 'theme.editor.token.sidebarForeground', descriptionKey: 'theme.editor.token.sidebarForegroundDesc', cssVar: '--sidebar-foreground', group: 'sidebar' },
      { key: 'sidebarBorder', labelKey: 'theme.editor.token.sidebarBorder', descriptionKey: 'theme.editor.token.sidebarBorderDesc', cssVar: '--sidebar-border', group: 'sidebar' },
      { key: 'sidebarAccent', labelKey: 'theme.editor.token.sidebarAccent', descriptionKey: 'theme.editor.token.sidebarAccentDesc', cssVar: '--sidebar-accent', group: 'sidebar' },
      { key: 'sidebarAccentForeground', labelKey: 'theme.editor.token.sidebarAccentForeground', descriptionKey: 'theme.editor.token.sidebarAccentForegroundDesc', cssVar: '--sidebar-accent-foreground', group: 'sidebar' },
    ],
  },
  {
    id: 'rail',
    labelKey: 'theme.editor.group.rail',
    tokens: [
      { key: 'rail', labelKey: 'theme.editor.token.rail', descriptionKey: 'theme.editor.token.railDesc', cssVar: '--rail', group: 'rail' },
      { key: 'railForeground', labelKey: 'theme.editor.token.railForeground', descriptionKey: 'theme.editor.token.railForegroundDesc', cssVar: '--rail-foreground', group: 'rail' },
      { key: 'railBorder', labelKey: 'theme.editor.token.railBorder', descriptionKey: 'theme.editor.token.railBorderDesc', cssVar: '--rail-border', group: 'rail' },
    ],
  },
];
